import { FC, MouseEvent, useCallback } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useMobileBreakpoint } from '@customink/pigment-react';
import { useFeatureFlag } from '~/adapters/optimizely/accounts';
import { trackEvent } from '~/adapters/tracking/tracking';
import setLocation from '~/adapters/browser/setLocation';
import { useFundraiserLink, useGroupOrdersLink } from '../links';
import { gaCategoryOld } from '../tracking';
import { AddToCartMenuItem, AddToCartMenuItemProps } from './AddToCartMenuItem';

type CardMenuProps = AddToCartMenuItemProps & { anchorElement: HTMLElement | null };

export const OrderOptionsMenu: FC<CardMenuProps> = ({
    anchorElement,
    handleClose,
    design,
    cardIndex,
    buyWaiting,
    setBuyWaiting,
}) => {
    const isMobile = useMobileBreakpoint();
    const orderOptionsVariant = useFeatureFlag('order_options_cta');

    const cid = design.compositeId;

    const groupOrdersLink = useGroupOrdersLink(cid);
    const fundRaisingLink = useFundraiserLink(cid);
    const navigateToGroupOrders = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            handleClose();
            e.preventDefault();
            trackEvent({ category: gaCategoryOld, action: 'Link Click', label: 'Start Group Order' });
            setLocation(groupOrdersLink);
        },
        [handleClose, setLocation, groupOrdersLink],
    );

    const navigateToFundraiser = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            handleClose();
            e.preventDefault();
            trackEvent({ category: gaCategoryOld, action: 'Link Click', label: 'Start a Fundraiser' });
            setLocation(fundRaisingLink);
        },
        [handleClose, setLocation, fundRaisingLink],
    );

    return (
        <Menu
            open={Boolean(anchorElement)}
            onClose={handleClose}
            anchorEl={anchorElement}
            anchorOrigin={{ vertical: isMobile ? 'bottom' : 'top', horizontal: isMobile ? 'left' : 'right' }}
            id="view-order-options-menu"
            MenuListProps={{ 'aria-labelledby': 'view-order-options-menu' }}
        >
            {orderOptionsVariant === 'test_c' && (
                <AddToCartMenuItem
                    design={design}
                    handleClose={handleClose}
                    cardIndex={cardIndex}
                    buyWaiting={buyWaiting}
                    setBuyWaiting={setBuyWaiting}
                />
            )}
            <MenuItem onClick={navigateToGroupOrders}>
                {design.usedInGroupOrderForm ? 'Manage Group Order' : 'Set up Group Order'}
            </MenuItem>
            <MenuItem onClick={navigateToFundraiser}>Start a Fundraiser</MenuItem>
        </Menu>
    );
};
